import { useState, useEffect } from "react";
import { Textarea } from "@/components/ui/Textarea";
import { Button } from "@/components/ui/Button";
import * as Diff from "diff";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { RotateCcw, Trash2 } from "lucide-react";

const SAMPLE_ORIGINAL = "The quick brown fox jumps over the lazy dog in the sunny afternoon.";
const SAMPLE_MODIFIED = "The extremely fast brown fox jumps over the sleeping dog on a hot summer afternoon.";

export function TextDiffChecker() {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [diffResult, setDiffResult] = useState<Diff.Change[]>([]);

  const compare = (val1 = text1, val2 = text2) => {
    const diff = Diff.diffWords(val1, val2);
    setDiffResult(diff);
  };

  useEffect(() => {
    setText1(SAMPLE_ORIGINAL);
    setText2(SAMPLE_MODIFIED);
    compare(SAMPLE_ORIGINAL, SAMPLE_MODIFIED);
  }, []);

  return (
    <ToolPageLayout
      title="Text Diff Checker Online - Compare Two Documents"
      description="Compare two text documents to find differences instantly. Highlight additions and removals with our free online diff checker."
      h1="Text Diff Checker"
      category="Text & Word"
      categoryPath="/text-word"
      howToUse={[
        "Paste your original text in the left box.",
        "Paste the modified text in the right box.",
        "Click 'Compare' to see the differences highlighted."
      ]}
      faq={[
        { question: "How to compare two text files?", answer: "Paste them into our diff checker tool and it will instantly highlight the additions in green and removals in red." },
        { question: "Is there an online diff checker?", answer: "Yes, our online text diff checker tool is completely free and works right in your browser." }
      ]}
    >
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-1 block">Original Text</label>
            <Textarea value={text1} onChange={e => { setText1(e.target.value); compare(e.target.value, text2); }} className="min-h-[200px]" />
          </div>
          <div>
            <label className="text-sm font-medium mb-1 block">Modified Text</label>
            <Textarea value={text2} onChange={e => { setText2(e.target.value); compare(text1, e.target.value); }} className="min-h-[200px]" />
          </div>
        </div>
        
        <div className="flex gap-2">
          <Button onClick={() => compare()} className="bg-slate-900 hover:bg-slate-800 text-white">Compare Texts</Button>
          <Button 
            variant="outline" 
            onClick={() => {
              setText1(SAMPLE_ORIGINAL);
              setText2(SAMPLE_MODIFIED);
              compare(SAMPLE_ORIGINAL, SAMPLE_MODIFIED);
            }}
            className="gap-1.5"
          >
            <RotateCcw className="w-4 h-4" />
            Reset Sample
          </Button>
          <Button 
            variant="ghost" 
            onClick={() => { setText1(""); setText2(""); setDiffResult([]); }}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-1.5"
          >
            <Trash2 className="w-4 h-4" />
            Clear
          </Button>
        </div>

        {diffResult.length > 0 && (
          <div className="p-4 border border-slate-200 rounded-xl bg-slate-50 min-h-[100px] whitespace-pre-wrap leading-relaxed">
            {diffResult.map((part, index) => {
              const color = part.added 
                ? "bg-emerald-100 text-emerald-900 px-0.5 rounded font-bold" 
                : part.removed 
                  ? "bg-red-100 text-red-900 line-through px-0.5 rounded" 
                  : "text-slate-700";
              return <span key={index} className={color}>{part.value}</span>;
            })}
          </div>
        )}
      </div>
    </ToolPageLayout>
  );
}
